import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Plot from 'react-plotly.js';
import { OverlayTrigger, Popover } from 'react-bootstrap';
import { ManText } from '@craft-design-system/core-components-react';
import PopoverComponent from '../popover';
import SegmentedControl_totalChargingTime from './SegmentedControl_totalChargingTime';
import useSegmentedChartQuery from '../../utils/useSegmentedChartQuery';
import Shimmer_graph from '../shimmer_graph';
import Zoom from '../Zoom';
import DownloadButtonComponent from '../DownloadButtonComponent';
import ifav from './ifav.png';

const Charging_cycle_per_charging_session = ({ value }) => {
  const { vin_short, chargingCyclePerSession } = value;

  const [chartType, setChartType] = useState("histogram");
  
  const [charging_cycle_per_session_total, set_charging_cycle_per_session_total] = useState(chargingCyclePerSession?.[0] ? JSON.parse(chargingCyclePerSession[0]) : { data: [], layout: {} });
  const [downloadData, setDownloadData] = useState(chargingCyclePerSession?.[1] ? JSON.parse(chargingCyclePerSession[1]) : []);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const { fetchSegmentedChart } = useSegmentedChartQuery();
  const tooltipContent = chartType === "histogram"
    ? `
    <b>Definition</b><br/>
    
    This KPI displays the <b>number of charging cycles within a charging session</b>. Fast and normal charging cycles are both taken into account.
    <br/>
    <b>Histogram</b><br/>

    Each bar represents the number of charging sessions that fall within a certain range (bin) of <b>charging cycles within a charging session values.</b> 

    All values since start of data acquisition for the selected vehicle(s) are visualized per default.`
    : `Boxplot

    The boxplot shows the <b>distribution of charging cycles within a charging session values</b> for the selected vehicle(s) since start of data acquisition. If multiple vehicles are selected, the boxplot reflects the combined distribution of all their values.
    <ul>
    <li>The box represents the middle 50% of values (from the 25th percentile ("q1") to the 75th percentile ("q3")).<li>
    <li>The line inside the box shows the median (50th percentile).</li>
    <li>The whiskers extend to show the range of typical values ("min" and "max") and visualize the spread of the data beyond the central box.</li>
    </ul>`;

    const popover = (
      <Popover id="popover-basic">
        <Popover.Body>
          <strong>Definition:</strong><br />
          This KPI displays the number of charging cycles within a charging session. Fast and normal charging cycles are both taken into account.<br /><br />
    
          <strong>Telematic raw data for this KPI:</strong><br />
          TBM3:<br />
          • highvoltsysmode<br />
          • batterycurrent<br />
          • energylevel<br />
          CM4:<br />
          • highvoltsysmode<br />
          • batterycurrent<br />
          • batcurrcbp<br />
          • energylevel<br />
          • energylevelcbp<br /><br />

    
          <strong>Histogram:</strong><br />
          Each bar represents the number of charging sessions that fall within a certain range (bin) of charging cycles within a charging session values.<br />
          All values since start of data acquisition for the selected vehicle(s) are visualized per default.<br /><br />
    
          <strong>Boxplot:</strong><br />
          The boxplot shows the distribution of charging cycles within a charging session values for the selected vehicle(s) since start of data acquisition.<br />
          If multiple vehicles are selected, the boxplot reflects the combined distribution of all their values.<br />
          • The <strong>box</strong> represents the middle 50% of values (from the 25th percentile ("q1") to the 75th percentile ("q3")).<br />
          • The line <strong>inside the box</strong> shows the median (50th percentile).<br />
          • The whiskers extend to show the range of typical values ("min" and "max") and visualize <strong>the spread of the data</strong> beyond the central box.
        </Popover.Body>
      </Popover>
    );

  // Load fallback data only once
  useEffect(() => {
    if (!charging_cycle_per_session_total && chargingCyclePerSession) {
      try {
        const parsed = JSON.parse(chargingCyclePerSession);
        set_charging_cycle_per_session_total(parsed);
      } catch (error) {
        console.error('Error parsing fallback charging_time JSON', error);
      }
    }
  }, [chargingCyclePerSession, charging_cycle_per_session_total]);

  // Fetch data immediately on mount and chartType change
  useEffect(() => {
    const fetchData = async () => {
      if (!vin_short?.length) return;

      setIsLoading(true);
      try {
        const data = await fetchSegmentedChart({
          vinShort: value.vin_short,
          chartType,
          columnname: 'Charging Cycle Per Session',
        });

        set_charging_cycle_per_session_total(JSON.parse(data[0]));   // chart
        setDownloadData(JSON.parse(data[1]));        // download
      } catch (error) {
        console.error('Error fetching chart data:', error);
        set_charging_cycle_per_session_total({ data: [], layout: {} });
        setDownloadData([]);
      } finally {
        setIsLoading(false);
      }
    };

    if (!isInitialLoad) {
      fetchData();
    } else {
      setIsInitialLoad(false);
    }
  }, [chartType, JSON.stringify(vin_short)]);


  return (
    <div style={{ padding: '16px' }}>
      {/* Title and Info */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '8px',
        gap: '8px'
      }}>
        <div style={{
          flex: 1,
          textAlign: 'center',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <ManText
            className="card-title-text"
            variant="component-l"
            as="span"
            size="l"
            style={{ lineHeight: 'normal' }}
          >
            Charging cycle per charging session
          </ManText>
        </div>
        <div style={{
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          height: '100%',
          flexDirection: "row-reverse",
          gap: "8px"
        }}>
          {/* <PopoverComponent
            id="charging_time"
            content={tooltipContent}
          /> */}
          <OverlayTrigger trigger="hover" placement="left" overlay={popover}>
            <img className="man-logovv1" src={ifav} alt="info" />
          </OverlayTrigger>
          <Zoom
            chartData={charging_cycle_per_session_total?.data}
            chartLayout={charging_cycle_per_session_total?.layout}
            chartTitle="Charging cycle per charging session"
          />
          <DownloadButtonComponent
            data={downloadData}
            fileName={`charging_cycle_per_session_${chartType.toLowerCase()}.xlsx`}
            disabled={!downloadData || downloadData.length === 0}
          />
        </div>
      </div>

      {/* Segmented Control */}
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <SegmentedControl_totalChargingTime
          selected={chartType}
          onChange={setChartType}
          vinShort={Array.isArray(vin_short) ? vin_short : [vin_short]}
          allowedTypes={["histogram", "boxplot"]}
        />
      </div>

      {/* Chart */}
      {isLoading ? (
        <Shimmer_graph />
      ) : charging_cycle_per_session_total?.data && charging_cycle_per_session_total?.layout ? (
        <div style={{ width: '100%', height: '420px', overflow: 'hidden' }}>
          <div style={{ width: '100%', height: '100%' }}>
            <Plot
              data={charging_cycle_per_session_total.data}
              layout={{
                ...charging_cycle_per_session_total.layout,
                autosize: true,
                responsive: true,
                margin: { t: 10, r: 10, l: 10, b: 160 },
              }}
              style={{ width: '100%', height: '100%' }}
              useResizeHandler={true}
            />
          </div>
        </div>
      ) : (
        <div style={{ textAlign: "center", padding: "20px" }}>
          <ManText className='card-title-text' variant='component-l' as='span' size='l'>
            {charging_cycle_per_session_total ? `${charging_cycle_per_session_total} h` : 'No Data Available'}
          </ManText>
        </div>
      )}
    </div>
  );
};

Charging_cycle_per_charging_session.propTypes = {
  value: PropTypes.shape({
    chargingCyclePerSession: PropTypes.string,
    vin_short: PropTypes.array,
  }).isRequired,
};

export default Charging_cycle_per_charging_session;

